/**
 * InsightPro™ — Client Onboarding Wizard
 * /admin/onboard
 *
 * Drop into: portal/src/pages/admin/OnboardWizard.jsx
 * Add to router: <Route path="/admin/onboard" element={<OnboardWizard />} />
 *
 * Reads:  Firebase Auth token (superAdmin claim required)
 * Writes: POST /provisionCompany  → Firestore + BigQuery
 *         POST /inviteUser        → Firebase Auth + magic link per user
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import * as XLSX from 'xlsx';

const FN_BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL
  || 'https://africa-south1-insightprofinal-3c793.cloudfunctions.net';

// ─── API helper ────────────────────────────────────────────
async function callFn(name, body) {
  // Force refresh ensures the token contains the latest custom claims
  // (superAdmin role required by provisionCompany / inviteUser)
  const token = await getAuth().currentUser?.getIdToken(true);
  const res = await fetch(`${FN_BASE}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `${name} failed`);
  return data;
}

// ─── Excel import helper ───────────────────────────────────
function parseExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(ws));
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
}

// ─── Default permission template ─────────────────────────
const EMPTY_PERMISSIONS = {
  executiveOverview: { view: false, export: false },
  sales:             { view: false, export: false },
  margin:            { view: false, export: false },
  stock:             { view: false, export: false },
  debtors:           { view: false, export: false },
  branches:          { view: false, export: false },
  advisory:          { view: false, export: false },
  actions:           { view: false, edit:   false },
  reports:           { view: false },
};

const PRESET_EXECUTIVE = {
  executiveOverview: { view: true,  export: true  },
  sales:             { view: true,  export: true  },
  margin:            { view: true,  export: true  },
  stock:             { view: true,  export: true  },
  debtors:           { view: true,  export: true  },
  branches:          { view: true,  export: true  },
  advisory:          { view: true,  export: false },
  actions:           { view: true,  edit:   true  },
  reports:           { view: true },
};

const PRESET_BRANCH_MGR = {
  executiveOverview: { view: true,  export: false },
  sales:             { view: true,  export: true  },
  margin:            { view: false, export: false },
  stock:             { view: true,  export: true  },
  debtors:           { view: false, export: false },
  branches:          { view: true,  export: false },
  advisory:          { view: false, export: false },
  actions:           { view: true,  edit:   false },
  reports:           { view: true },
};

const PRESET_FINANCE = {
  executiveOverview: { view: true,  export: true  },
  sales:             { view: true,  export: true  },
  margin:            { view: true,  export: true  },
  stock:             { view: false, export: false },
  debtors:           { view: true,  export: true  },
  branches:          { view: false, export: false },
  advisory:          { view: false, export: false },
  actions:           { view: false, edit:   false },
  reports:           { view: true },
};

const MODULE_LABELS = [
  { key: 'executiveOverview', label: 'Executive Overview', hasEdit: false },
  { key: 'sales',             label: 'Sales & Revenue',    hasEdit: false },
  { key: 'margin',            label: 'Margin Analysis',    hasEdit: false },
  { key: 'stock',             label: 'Stock & Inventory',  hasEdit: false },
  { key: 'debtors',           label: 'Debtors & Credit',   hasEdit: false },
  { key: 'branches',          label: 'Branch Performance', hasEdit: false },
  { key: 'advisory',          label: 'Advisory Notes',     hasEdit: false },
  { key: 'actions',           label: 'Action Tracking',    hasEdit: true  },
  { key: 'reports',           label: 'Reports / Power BI', hasEdit: false },
];

// ── Multi-Entity Finance permissions / preset / brand list ──
const FINANCE_PERMISSIONS = {
  groupPL:          { view: false, export: false },
  balanceSheet:     { view: false, export: false },
  expenseAnalysis:  { view: false, export: false },
  cashFlow:         { view: false, export: false },
  storePerformance: { view: false, export: false },
  dcPerformance:    { view: false, export: false },
  variance:         { view: false, export: false },
  groupConsol:      { view: false, export: false },
  notes:            { view: false, export: false },
};

const PRESET_GROUP_CFO = {
  groupPL:          { view: true, export: true },
  balanceSheet:     { view: true, export: true },
  expenseAnalysis:  { view: true, export: true },
  cashFlow:         { view: true, export: true },
  storePerformance: { view: true, export: true },
  dcPerformance:    { view: true, export: true },
  variance:         { view: true, export: true },
  groupConsol:      { view: true, export: true },
  notes:            { view: true, export: true },
};

const FINANCE_MODULE_LABELS = [
  { key: 'groupPL',          label: 'Group P&L',           hasEdit: false },
  { key: 'balanceSheet',     label: 'Balance Sheet',       hasEdit: false },
  { key: 'expenseAnalysis',  label: 'Expense Analysis',    hasEdit: false },
  { key: 'cashFlow',         label: 'Cash Flow',           hasEdit: false },
  { key: 'storePerformance', label: 'Store Performance',   hasEdit: false },
  { key: 'dcPerformance',    label: 'DC Performance',      hasEdit: false },
  { key: 'variance',         label: 'Actual vs Budget',    hasEdit: false },
  { key: 'groupConsol',      label: 'Group Consolidated',  hasEdit: false },
  { key: 'notes',            label: 'Notes & Commentary',  hasEdit: false },
];

const BRAND_OPTIONS = ['Consolidated', 'Pedros', 'BirdAndCo', 'Wackys'];

const STEPS = [
  { label: 'Company',              desc: 'Core details & plan'         },
  { label: 'Branches & Locations', desc: 'Stores, warehouses, offices' },
  { label: 'User Groups',          desc: 'Permissions & access'        },
  { label: 'Users',                desc: 'Invite & assign'             },
  { label: 'Power BI & Schema',    desc: 'Dataset & column mappings'   },
  { label: 'Reports',              desc: 'Power BI assignments'        },
  { label: 'Security',             desc: 'Access & policy'             },
  { label: 'Review & Launch',      desc: 'Confirm & provision'         },
];

// Finance variant — same step count, different labels for steps 2/5/6.
const STEPS_FINANCE = [
  { label: 'Company',              desc: 'Core details & plan'         },
  { label: 'Legal Entities',       desc: 'Brands, entities, currency'  },
  { label: 'User Groups',          desc: 'Permissions & access'        },
  { label: 'Users',                desc: 'Invite & assign brand'       },
  { label: 'Chart of Accounts',    desc: 'GL accounts upload'          },
  { label: 'Reports',              desc: 'Built-in pages'              },
  { label: 'Security',             desc: 'Access & policy'             },
  { label: 'Review & Launch',      desc: 'Confirm & provision'         },
];

// ── Default schema (MFA column names — edit for each client) ─
const DEFAULT_SCHEMA = {
  txTable:'Transactions', dateCol:'TXDATE', salesCol:'SALES', purchasesCol:'PURCHASES',
  unitsCol:'UNITS_SOLD', codeCol:'CODE',
  branchTable:'CompanyList', branchNameCol:'COMPANY NAME', branchConoCol:'CONO',
  stockTable:'Stock', stockCodeCol:'StockCode', stockDescCol:'Description',
  stockSellCol:'SELLPRICE1', stockCostCol:'AVRGCOST', stockOnHandCol:'ONHAND', stockDeptCol:'DEPARTMENT',
  stockBalTable:'StockBalances', stockBalCodeCol:'CODE', stockBalOnHandCol:'ONHAND',
  stockBalLaybyeCol:'LAYBYE', stockBalConoCol:'CONO',
  deptTable:'Departments', deptCodeCol:'Dept', deptNameCol:'DeptName',
  debtorTable:'Debtors', debtorAcctCol:'ACCOUNT', debtorNameCol:'NAME',
  debtorTotalCol:'TOTAL', debtorCurrentCol:'CURRENT',
  debtor30Col:'DAYS_30', debtor60Col:'DAYS_60', debtor90Col:'DAYS_90',
  debtor120Col:'DAYS_120', debtor150Col:'DAYS_150', debtor180Col:'DAYS_180',
  debtorOnHoldCol:'ONHOLD', debtorCrLimitCol:'CRLIMIT',
  debtorLastPayCol:'DATELASTPAY', debtorLastAmtCol:'AMOUNTLASTPAYMENT',
  dateTable:'Date', dateYYYYMMCol:'YYYYMM', dateMonthNameCol:'MonthName', dateYearCol:'Year',
};

// ══════════════════════════════════════════════════════════════
export default function OnboardWizard() {
  const [step, setStep] = useState(1);
  const [launching, setLaunching] = useState(false);
  const [launched, setLaunched]   = useState(null);
  const [launchError, setLaunchError] = useState('');

  // ── Step 1: Company ──────────────────────────────────────
  const [company, setCompany] = useState({
    name: '', tradingName: '', regNumber: '', industry: '',
    solution: 'RetailPOS',                          // RetailPOS | MultiEntityFinance
    country: 'South Africa', timezone: 'Africa/Johannesburg',
    plan: 'professional', billingEmail: '', logoUrl: '',
    primaryContact: { name: '', email: '', phone: '' },
  });
  const isFinance = company.solution === 'MultiEntityFinance';

  // ── Logo upload state ────────────────────────────────────
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoProgress,  setLogoProgress]  = useState(0);
  const [logoError,     setLogoError]     = useState('');
  const logoInputRef = useRef(null);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) { setLogoError('File too large — max 2MB'); return; }
    if (!file.type.startsWith('image/')) { setLogoError('Must be an image file (PNG, JPG, SVG)'); return; }
    setLogoUploading(true); setLogoError(''); setLogoProgress(0);
    try {
      const storage  = getStorage();
      const slug     = company.name.toLowerCase().replace(/[^a-z0-9]+/g, '_') || 'client';
      const ext      = file.name.split('.').pop();
      const storageRef = ref(storage, `logos/${slug}_${Date.now()}.${ext}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed',
        snap => setLogoProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        err  => { setLogoError(err.message); setLogoUploading(false); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setCompany(c => ({ ...c, logoUrl: url }));
          setLogoUploading(false); setLogoProgress(0);
        }
      );
    } catch (e) { setLogoError(e.message); setLogoUploading(false); }
  };

  // ── Step 2: Branches / Legal Entities ───────────────────
  // For RetailPOS this is physical branches.
  // For MultiEntityFinance the same array holds legal entities — rows carry
  // brand / entityType / country / currency / sageCoName attributes.
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState({
    code:'', name:'', type:'Store', city:'', region:'', country:'ZA',
    // finance-only attrs (ignored for RetailPOS)
    brand:'', entityType:'Store', currency:'ZAR', sageCoName:'',
  });

  // ── Step 3: User Groups ─────────────────────────────────
  const [userGroups, setUserGroups] = useState([]);
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);
  const [newGroupName, setNewGroupName] = useState('');

  // ── Step 4: Users ───────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    firstName:'', lastName:'', email:'', phone:'', groupId:'', branchId:'',
    brand:'Consolidated',   // finance-only, ignored for RetailPOS
    sendInvite: true,
  });
  // Map of lowercase-email → true if that email already has a Firebase Auth account.
  // Populated on email blur (current row) and after importUsers. Drives the
  // "Existing user — will be linked, no invite sent" chip in the Users table.
  const [existingEmails, setExistingEmails] = useState({});
  // Per-email check state for the inline hint under the Email input: 'idle' | 'checking' | 'new' | 'existing'
  const [newUserEmailStatus, setNewUserEmailStatus] = useState('idle');

  // ── Step 5: Power BI & Schema ───────────────────────────
  const [pbi, setPbi] = useState({
    workspaceId: '', datasetId: '', serviceAccount: '',
    schema: { ...DEFAULT_SCHEMA },
  });

  // ── Step 5 (finance variant): Chart of Accounts ─────────
  const [glAccounts, setGlAccounts] = useState([]);
  const importGlAccounts = async e => {
    const rows = await parseExcel(e.target.files[0]);
    const mapped = rows.map(r => ({
      glAccount:      String(r.GLAccount     || r.glAccount     || '').trim(),
      glDescription:  r.GLDescription || r.glDescription || '',
      accountType:    r.AccountType   || r.accountType   || '',
      reportCategory: r.ReportCategory|| r.reportCategory|| '',
      brand:          r.Brand         || r.brand         || '',
      normalSign:     Number(r.NormalSign ?? r.normalSign ?? 1) || 1,
    })).filter(a => a.glAccount && a.brand);
    setGlAccounts(g => [...g, ...mapped]);
  };

  // ── Step 6: Reports ─────────────────────────────────────
  const [reports, setReports] = useState([]);
  const [newReport, setNewReport] = useState({ name:'', powerBiReportId:'', powerBiWorkspaceId:'', visibleToGroups:[], defaultForGroups:[] });

  // ── Step 6: Security ────────────────────────────────────
  const [security, setSecurity] = useState({
    sessionTimeoutMins: 60, mfaRequired: false, magicLinkOnly: true,
    maxFailedLogins: 5, exportAllowed: true, auditEnabled: true,
    auditRetentionDays: 90, loginHoursEnabled: false,
    loginHoursStart: '07:00', loginHoursEnd: '18:00', ipWhitelist: [],
  });

  // ─── Mobile detection ─────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ─── Step validation ──────────────────────────────────────
  const stepErrors = {
    1: !company.name.trim() ? 'Company name is required to continue.' : null,
    2: branches.length === 0
        ? (isFinance ? 'Add at least one legal entity to continue.' : 'Add at least one branch to continue.')
        : null,
    3: userGroups.length === 0 ? 'Add at least one user group to continue.' : null,
    4: users.length === 0 ? 'Add at least one user to continue.' : null,
    5: isFinance
        ? (glAccounts.length === 0 ? 'Upload the chart of accounts to continue.' : null)
        : (!pbi.workspaceId.trim() || !pbi.datasetId.trim() ? 'Power BI Workspace ID and Dataset ID are required.' : null),
    6: null,
    7: null,
  };
  const currentStepError = stepErrors[step] ?? null;

  const canAdvance = !currentStepError;

  const handleNext = () => {
    if (!canAdvance) return;
    setStep(s => Math.min(STEPS.length, s + 1));
    if (isMobile) setSidebarOpen(false);
  };

  // ─── helpers ──────────────────────────────────────────────
  const companySlug = company.name
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const pct = Math.round(((step - 1) / (STEPS.length - 1)) * 100);

  // ─── Branch / Entity helpers ──────────────────────────────
  const addBranch = () => {
    if (!newBranch.code || !newBranch.name) return;
    setBranches(b => [...b, { ...newBranch, active: true }]);
    setNewBranch({
      code:'', name:'', type:'Store', city:'', region:'', country:'ZA',
      brand:'', entityType:'Store', currency:'ZAR', sageCoName:'',
    });
  };

  // Single import handler — picks up POS columns (BranchCode/Name/...) AND
  // finance columns (EntityCode/LegalName/Brand/EntityType/Currency/SageCoName)
  // depending on which template the user uploaded. Works for both modes.
  const importBranches = async e => {
    const rows = await parseExcel(e.target.files[0]);
    const mapped = rows.map(r => ({
      code:       String(r.EntityCode || r.BranchCode || r.code || '').toUpperCase(),
      name:       r.LegalName  || r.BranchName  || r.name  || '',
      type:       r.EntityType || r.Type || r.type || 'Store',
      city:       r.City        || r.city  || '',
      // Province is the BQ DimEntity column. Region is the legacy Firestore field.
      // Accept either header on import and propagate to both.
      province:   r.Province    || r.province || r.Region || r.region || '',
      region:     r.Region      || r.region   || r.Province || r.province || '',
      country:    r.Country     || r.country|| 'ZA',
      // finance attrs (no-op for RetailPOS rows missing them)
      brand:      r.Brand       || r.brand || '',
      entityType: r.EntityType  || r.Type  || 'Store',
      currency:   r.Currency    || 'ZAR',
      sageCoName: r.SageCoName  || '',
      active:     (r.Active || r.active || 'Yes').toString().toLowerCase() !== 'no',
    })).filter(b => b.code && b.name);
    setBranches(b => [...b, ...mapped]);
  };

  // ─── Group helpers ────────────────────────────────────────
  const addGroup = () => {
    if (!newGroupName) return;
    // For finance clients, include the finance permission keys in the empty template.
    const emptyPerms = isFinance
      ? { ...EMPTY_PERMISSIONS, ...FINANCE_PERMISSIONS }
      : { ...EMPTY_PERMISSIONS };
    setUserGroups(g => [...g, {
      name: newGroupName, description: '',
      branchScope: 'all',
      permissions: emptyPerms,
      dataAccess: { periods:'all', priorYear:true, whatsappDigest:false, alertNotifications:false },
    }]);
    setNewGroupName('');
    setActiveGroupIdx(userGroups.length);
  };

  const setGroupField = (idx, field, val) => {
    setUserGroups(g => g.map((grp, i) => i === idx ? { ...grp, [field]: val } : grp));
  };

  const setGroupPerm = (idx, module, col, val) => {
    setUserGroups(g => g.map((grp, i) => {
      if (i !== idx) return grp;
      return { ...grp, permissions: {
        ...grp.permissions,
        [module]: { ...grp.permissions[module], [col]: val }
      }};
    }));
  };

  const applyPreset = (idx, preset) => {
    setUserGroups(g => g.map((grp, i) =>
      i === idx ? { ...grp, permissions: { ...preset } } : grp
    ));
  };

  // ─── User helpers ─────────────────────────────────────────

  // Checks whether an email already has a Firebase Auth account.
  // Returns true/false, or null on error (treat unknown as "new" for display).
  const checkEmailExists = async (rawEmail) => {
    const email = (rawEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return null;
    // Cached?
    if (existingEmails[email] !== undefined) return existingEmails[email];
    try {
      const res = await callFn('checkUserExists', { email });
      setExistingEmails(m => ({ ...m, [email]: !!res.exists }));
      return !!res.exists;
    } catch {
      return null;
    }
  };

  const handleNewUserEmailBlur = async () => {
    const email = (newUser.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { setNewUserEmailStatus('idle'); return; }
    setNewUserEmailStatus('checking');
    const exists = await checkEmailExists(email);
    setNewUserEmailStatus(exists === null ? 'idle' : (exists ? 'existing' : 'new'));
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.firstName) return;
    // Ensure we have a check result before adding — the table reads from existingEmails
    await checkEmailExists(newUser.email);
    setUsers(u => [...u, { ...newUser }]);
    setNewUser({ firstName:'', lastName:'', email:'', phone:'', groupId:'', branchId:'', sendInvite:true });
    setNewUserEmailStatus('idle');
  };

  const importUsers = async e => {
    const rows = await parseExcel(e.target.files[0]);
    const mapped = rows.map(r => ({
      firstName:  r.FirstName  || r.firstName  || '',
      lastName:   r.LastName   || r.lastName   || '',
      email:      r.Email      || r.email      || '',
      phone:      r.Phone      || r.phone      || '',
      groupId:    r.UserGroup  || r.groupId    || '',
      branchId:   r.BranchCode === 'ALL' ? null : (r.BranchCode || r.branchId || null),
      sendInvite: (r.SendInvite || 'Yes').toString().toLowerCase() !== 'no',
    })).filter(u => u.email && u.firstName);
    setUsers(u => [...u, ...mapped]);
    // Check all imported emails in parallel so the list shows linked/invite status
    await Promise.all(mapped.map(u => checkEmailExists(u.email)));
  };

  // ─── Report helpers ───────────────────────────────────────
  const addReport = () => {
    if (!newReport.powerBiReportId) return;
    setReports(r => [...r, { ...newReport }]);
    setNewReport({ name:'', powerBiReportId:'', powerBiWorkspaceId:'', visibleToGroups:[], defaultForGroups:[] });
  };

  const importReports = async e => {
    const rows = await parseExcel(e.target.files[0]);
    const mapped = rows.map(r => ({
      name:               r.ReportName          || r.name || '',
      powerBiReportId:    r.PowerBIReportID     || r.powerBiReportId || '',
      powerBiWorkspaceId: r.PowerBIWorkspaceID  || r.powerBiWorkspaceId || '',
      visibleToGroups:    (r.VisibleToGroups || '').split(',').map(s => s.trim()).filter(Boolean),
      defaultForGroups:   (r.DefaultForGroups|| '').split(',').map(s => s.trim()).filter(Boolean),
    })).filter(r => r.powerBiReportId);
    setReports(r => [...r, ...mapped]);
  };

  // ─── LAUNCH ───────────────────────────────────────────────
  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError('');
    try {
      // 1. Provision company
      const result = await callFn('provisionCompany', {
        company: { ...company, slug: companySlug },
        branches,
        userGroups,
        reports,
        security,
        // PBI fields are blank/unused for MultiEntityFinance — server will skip DAX copy.
        pbi: {
          workspaceId:    pbi.workspaceId.trim(),
          datasetId:      pbi.datasetId.trim(),
          serviceAccount: pbi.serviceAccount.trim(),
          schema:         pbi.schema,
          configuredAt:   new Date().toISOString(),
        },
        // Finance-only payload (ignored by server when solution=RetailPOS).
        glAccounts: isFinance ? glAccounts : [],
      });

      const { companyId, groupIds = {} } = result;

      // 2. Invite users sequentially (avoid rate limit)
      const inviteResults = [];
      for (const u of users) {
        // Resolve group name → Firestore group ID using groupIds map from provisionCompany
        const groupId = groupIds[u.groupId] || u.groupId || null;
        try {
          const inv = await callFn('inviteUser', {
            companyId,
            firstName:  u.firstName,
            lastName:   u.lastName,
            email:      u.email,
            phone:      u.phone,
            groupId,
            branchId:   u.branchId || null,
            sendInvite: u.sendInvite,
          });
          inviteResults.push({ email: u.email, status: 'invited', uid: inv.uid });
        } catch (e) {
          inviteResults.push({ email: u.email, status: 'error', error: e.message });
        }
      }

      setLaunched({ companyId, ...result, inviteResults });
    } catch (e) {
      setLaunchError(e.message);
    } finally {
      setLaunching(false);
    }
  };

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════
  if (launched) return <LaunchSuccess data={launched} />;

  return (
    <div style={S.shell}>
      {/* ── Mobile top bar ── */}
      {isMobile && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:200,
          background:'#111116', borderBottom:'1px solid rgba(255,255,255,0.07)',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'12px 16px', height:52,
        }}>
          <div style={{fontFamily:'monospace', fontSize:10, color:'rgba(255,255,255,0.4)', letterSpacing:'0.14em', textTransform:'uppercase'}}>
            Step {step} of {STEPS.length} — {(isFinance ? STEPS_FINANCE : STEPS)[step-1].label}
          </div>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            style={{background:'none', border:'1px solid rgba(255,255,255,0.12)', borderRadius:4, color:'rgba(255,255,255,0.5)', padding:'5px 10px', cursor:'pointer', fontSize:14}}
          >{sidebarOpen ? '✕' : '☰'}</button>
        </div>
      )}

      {/* ── Sidebar ── */}
      <aside style={{
        ...S.sidebar,
        ...(isMobile ? {
          position:'fixed', top:52, left:0, bottom:0, zIndex:150,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform 0.25s ease',
          overflowY:'auto',
        } : {}),
      }}>
        <div style={S.sidebarLabel}>Onboarding Steps</div>
        {(isFinance ? STEPS_FINANCE : STEPS).map((s, i) => {
          const n = i + 1;
          const active = n === step;
          const done   = n < step;
          // Can only jump to completed steps or current step
          const locked = n > step;
          return (
            <div key={n}>
              <div
                style={{
                  ...S.stepItem,
                  ...(active ? S.stepActive : {}),
                  ...(done ? S.stepDone : {}),
                  ...(locked ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                onClick={() => {
                  if (locked) return;
                  setStep(n);
                  if (isMobile) setSidebarOpen(false);
                }}
              >
                <div style={{ ...S.stepNum, ...(active ? S.stepNumActive : {}), ...(done ? S.stepNumDone : {}) }}>
                  {done ? '✓' : n}
                </div>
                <div>
                  <div style={{ ...S.stepTitle, ...(active ? S.stepTitleActive : {}), ...(done ? S.stepTitleDone : {}) }}>
                    {s.label}
                  </div>
                  <div style={S.stepDesc}>{s.desc}</div>
                </div>
              </div>
              {n < STEPS.length && <div style={S.connector} />}
            </div>
          );
        })}

        {/* Mini summary */}
        <div style={S.sidebarFooter}>
          <div style={S.summaryLabel}>Current Client</div>
          <div style={S.summaryText}>{company.name || '—'}</div>
          <div style={S.summaryText}>Solution: {isFinance ? 'Finance' : 'Retail POS'}</div>
          <div style={S.summaryText}>Plan: {company.plan}</div>
          <div style={S.summaryText}>{isFinance ? 'Entities' : 'Branches'}: {branches.length}</div>
          <div style={S.summaryText}>Groups: {userGroups.length}</div>
          <div style={S.summaryText}>Users: {users.length}</div>
          <div style={S.summaryText}>{isFinance ? 'GL accts' : 'PBI'}: {isFinance ? glAccounts.length : (pbi.workspaceId ? '✓' : '—')}</div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div style={{
        ...S.main,
        ...(isMobile ? { marginTop: 52 } : {}),
      }}>
        <div style={{...S.content, padding: isMobile ? '16px' : '32px 40px'}}>

          {/* ════ STEP 1: COMPANY ════ */}
          {step === 1 && (
            <div>
              <StepHeader icon="🏢" title="Company Details"
                desc="Core company information. This creates the client account and BigQuery dataset." />

              <Section title="Identity">
                <Grid cols={isMobile ? 1 : 2}>
                  <Field label="Company Name *">
                    <Input value={company.name} onChange={v => setCompany(c => ({...c, name:v}))} placeholder="Company Name (Pty) Ltd" />
                  </Field>
                  <Field label="Trading Name">
                    <Input value={company.tradingName} onChange={v => setCompany(c => ({...c, tradingName:v}))} placeholder="Trading Name" />
                  </Field>
                  <Field label="Registration Number">
                    <Input value={company.regNumber} onChange={v => setCompany(c => ({...c, regNumber:v}))} placeholder="e.g. 2024/123456/07" />
                  </Field>
                  <Field label="Industry">
                    <Select value={company.industry} onChange={v => setCompany(c => ({...c, industry:v}))}
                      options={['Retail — General','Retail — Furniture','Retail — Food & Beverage','Manufacturing','Distribution','Services','Other']} />
                  </Field>
                  <Field label="Billing Email">
                    <Input value={company.billingEmail} onChange={v => setCompany(c => ({...c, billingEmail:v}))} placeholder="billing@client.co.za" />
                  </Field>
                  <Field label="BigQuery Dataset (auto)">
                    <Input value={`insightpro_${companySlug}`} disabled />
                  </Field>
                </Grid>

                {/* Solution selector — drives the rest of the wizard */}
                <div style={{marginTop:18}}>
                  <div style={{fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Solution</div>
                  <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:10}}>
                    {[
                      { key:'RetailPOS',           name:'Retail (POS)',           desc:'Sales · Stock · Debtors · Branch Performance.\nFor POS-driven retailers (Marburg, Goldtop pattern).' },
                      { key:'MultiEntityFinance',  name:'Multi-Entity Finance',   desc:'Group P&L · BS · Cash Flow · Store/DC Performance · Variance.\nFor multi-brand groups with Sage Evolution data (Pedros pattern).' },
                    ].map(opt => (
                      <div key={opt.key}
                        onClick={() => setCompany(c => ({...c, solution: opt.key}))}
                        style={{ ...S.planCard, ...(company.solution === opt.key ? S.planSelected : {}) }}
                      >
                        <div style={S.planName}>{opt.name}</div>
                        <div style={{...S.planFeatures, whiteSpace:'pre-line', marginTop:4}}>{opt.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Logo upload */}
                <div style={{marginTop:16}}>
                  <div style={{fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Company Logo (optional)</div>
                  <div style={{display:'flex', alignItems:'center', gap:16, flexWrap:'wrap'}}>
                    {/* Preview */}
                    <div style={{width:72, height:72, borderRadius:8, border:'1px solid #e4e6ec', background:'#f8f9fc', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden'}}>
                      {company.logoUrl
                        ? <img src={company.logoUrl} alt="Logo" style={{width:'100%', height:'100%', objectFit:'contain', padding:4}} />
                        : <span style={{fontSize:24}}>🏢</span>
                      }
                    </div>
                    <div style={{flex:1, minWidth:200}}>
                      <input
                        ref={logoInputRef}
                        type="file" accept="image/*"
                        style={{display:'none'}}
                        onChange={e => handleLogoUpload(e.target.files[0])}
                      />
                      <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:6}}>
                        <button type="button" style={S.btnImport} onClick={() => logoInputRef.current?.click()} disabled={logoUploading}>
                          {logoUploading ? `Uploading ${logoProgress}%...` : company.logoUrl ? '↺ Replace Logo' : '↑ Upload Logo'}
                        </button>
                        {company.logoUrl && (
                          <button type="button" style={{...S.btnImport, color:'#c44040', borderColor:'rgba(196,64,64,0.3)'}}
                            onClick={() => setCompany(c => ({...c, logoUrl:''}))}>
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      <div style={{fontSize:11, color:'#9898b0'}}>PNG, JPG or SVG · Max 2MB · Recommended: 400×400px square</div>
                      {logoError && <div style={{fontSize:11, color:'#c44040', marginTop:4}}>{logoError}</div>}
                      {logoUploading && (
                        <div style={{marginTop:6, height:3, background:'#e4e6ec', borderRadius:2}}>
                          <div style={{height:'100%', width:`${logoProgress}%`, background:'#c89520', borderRadius:2, transition:'width 0.2s'}}/>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Plan">
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap:12}}>
                  {[
                    { key:'essential',    price:'R 3,000/mo', features:'Dashboard · Sales · Stock\n1 report · Up to 5 users' },
                    { key:'professional', price:'R 5,000/mo', features:'All modules · Debtors\n3 reports · 15 users · WhatsApp' },
                    { key:'enterprise',   price:'R 8,000/mo', features:'All modules · Advisory\nUnlimited reports & users' },
                  ].map(p => (
                    <div key={p.key}
                      onClick={() => setCompany(c => ({...c, plan:p.key}))}
                      style={{ ...S.planCard, ...(company.plan === p.key ? S.planSelected : {}) }}
                    >
                      <div style={S.planName}>{p.key.charAt(0).toUpperCase() + p.key.slice(1)}</div>
                      <div style={S.planPrice}>{p.price}</div>
                      <div style={S.planFeatures}>{p.features}</div>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}

          {/* ════ STEP 2: BRANCHES (POS) or LEGAL ENTITIES (Finance) ════ */}
          {step === 2 && !isFinance && (
            <div>
              <StepHeader icon="📍" title="Branches & Locations"
                desc="Add all stores, warehouses and offices. Branch codes must match your POS/ERP system." />

              <ImportBar
                title="Import from Excel"
                sub="BranchCode, BranchName, Type, City, Region, Country, Active"
                onImport={importBranches}
                templateName="InsightPro_Branch_Template.xlsx"
              />

              <Section title={`Branches — ${branches.length} added`}>
                <DataTable
                  cols={['Code','Name','Type','City','Region','Active','']}
                  rows={branches.map((b,i) => [
                    <code>{b.code}</code>,
                    b.name,
                    <Badge type={b.type === 'Store' ? 'blue' : b.type === 'Warehouse' ? 'amber' : 'green'}>{b.type}</Badge>,
                    b.city, b.region,
                    <Badge type="green">Active</Badge>,
                    <button style={S.rowDel} onClick={() => setBranches(bs => bs.filter((_,j)=>j!==i))}>✕</button>
                  ])}
                />
                <Divider />
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr 1fr auto', gap:8, alignItems:'end'}}>
                  <Field label="Branch Code"><Input value={newBranch.code} onChange={v => setNewBranch(b=>({...b,code:v}))} placeholder="DBN001" /></Field>
                  <Field label="Name"><Input value={newBranch.name} onChange={v => setNewBranch(b=>({...b,name:v}))} placeholder="Durban Main" /></Field>
                  <Field label="Type">
                    <Select value={newBranch.type} onChange={v => setNewBranch(b=>({...b,type:v}))}
                      options={['Store','Warehouse','Office','Distribution Centre','Other']} />
                  </Field>
                  <Field label="City"><Input value={newBranch.city} onChange={v => setNewBranch(b=>({...b,city:v}))} placeholder="Durban" /></Field>
                  <Field label="Region"><Input value={newBranch.region} onChange={v => setNewBranch(b=>({...b,region:v}))} placeholder="KZN" /></Field>
                  <button style={S.addBtn} onClick={addBranch}>+ Add</button>
                </div>
              </Section>
            </div>
          )}

          {step === 2 && isFinance && (
            <div>
              <StepHeader icon="🏛️" title="Legal Entities & Hierarchy"
                desc="Add every legal entity in the group. Each entity = one Sage CoName. Brands are the consolidation grouping (Pedros / Bird & Co / Wackys)." />

              <ImportBar
                title="Import from Excel"
                sub="EntityCode, LegalName, Brand, EntityType, Country, Province, Currency, SageCoName"
                onImport={importBranches}
                templateName="InsightPro_Entity_Template.xlsx"
              />

              <Section title={`Legal Entities — ${branches.length} added`}>
                <DataTable
                  cols={['Code','Legal Name','Brand','Type','Country','Currency','Sage CoName','']}
                  rows={branches.map((b,i) => [
                    <code>{b.code}</code>,
                    b.name,
                    <Badge type={b.brand === 'Pedros' ? 'blue' : b.brand === 'Wackys' ? 'amber' : 'green'}>{b.brand || '—'}</Badge>,
                    b.entityType || b.type,
                    b.country,
                    b.currency || 'ZAR',
                    <span style={{fontFamily:'monospace', fontSize:11, color:'#6b6b80'}}>{b.sageCoName || '—'}</span>,
                    <button style={S.rowDel} onClick={() => setBranches(bs => bs.filter((_,j)=>j!==i))}>✕</button>
                  ])}
                />
                <Divider />
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 2fr 1fr 1fr 1fr 1fr 2fr auto', gap:8, alignItems:'end'}}>
                  <Field label="Code"><Input value={newBranch.code} onChange={v => setNewBranch(b=>({...b,code:v}))} placeholder="ACORN" /></Field>
                  <Field label="Legal Name"><Input value={newBranch.name} onChange={v => setNewBranch(b=>({...b,name:v}))} placeholder="Forty Five Star (Pty) Ltd" /></Field>
                  <Field label="Brand">
                    <Select value={newBranch.brand} onChange={v => setNewBranch(b=>({...b,brand:v}))}
                      options={['', ...BRAND_OPTIONS.filter(b => b !== 'Consolidated')]} />
                  </Field>
                  <Field label="Type">
                    <Select value={newBranch.entityType} onChange={v => setNewBranch(b=>({...b,entityType:v,type:v}))}
                      options={['Store','DC','HeadOffice','Marketing','Recruitment','Property']} />
                  </Field>
                  <Field label="Country">
                    <Select value={newBranch.country} onChange={v => setNewBranch(b=>({...b,country:v, currency: v==='BW'?'BWP':v==='ZM'?'ZMW':'ZAR'}))}
                      options={['ZA','BW','ZM']} />
                  </Field>
                  <Field label="Currency">
                    <Select value={newBranch.currency} onChange={v => setNewBranch(b=>({...b,currency:v}))}
                      options={['ZAR','BWP','ZMW']} />
                  </Field>
                  <Field label="Sage CoName"><Input value={newBranch.sageCoName} onChange={v => setNewBranch(b=>({...b,sageCoName:v}))} placeholder="Forty Five Star (Pty)Ltd t/a Pedros Acornhoek" /></Field>
                  <button style={S.addBtn} onClick={addBranch}>+ Add</button>
                </div>
              </Section>
            </div>
          )}

          {/* ════ STEP 3: USER GROUPS ════ */}
          {step === 3 && (
            <div>
              <StepHeader icon="🔐" title="User Groups & Permissions"
                desc="Define access groups. Each user will be assigned to a group." />

              {/* Group tabs */}
              <div style={S.tabs}>
                {userGroups.map((g,i) => (
                  <div key={i}
                    style={{ ...S.tab, ...(activeGroupIdx === i ? S.tabActive : {}) }}
                    onClick={() => setActiveGroupIdx(i)}
                  >{g.name}</div>
                ))}
                <div style={S.tab}>
                  <input
                    style={S.tabInput}
                    placeholder="+ New group"
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addGroup()}
                  />
                  {newGroupName && <button style={S.tabAddBtn} onClick={addGroup}>Add</button>}
                </div>
              </div>

              {userGroups[activeGroupIdx] && (() => {
                const g = userGroups[activeGroupIdx];
                const idx = activeGroupIdx;
                return (
                  <div>
                    <Grid cols={isMobile ? 1 : 2} style={{marginBottom:16}}>
                      <Field label="Group Name">
                        <Input value={g.name} onChange={v => setGroupField(idx,'name',v)} />
                      </Field>
                      <Field label="Description">
                        <Input value={g.description} onChange={v => setGroupField(idx,'description',v)} placeholder="e.g. Full access — all branches" />
                      </Field>
                    </Grid>

                    <Section title="Branch Scope">
                      <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        {[
                          ['all','🌐 All Branches'],
                          ['assigned-hard','📍 Assigned Branch — Hard Block'],
                          ['assigned-soft','🔒 Assigned Branch — Soft Block'],
                        ].map(([val, label]) => (
                          <div key={val}
                            style={{ ...S.scopeBtn, ...(g.branchScope === val ? S.scopeBtnSel : {}) }}
                            onClick={() => setGroupField(idx, 'branchScope', val)}
                          >{label}</div>
                        ))}
                      </div>
                    </Section>

                    <Section title="Start from preset">
                      <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        {(isFinance
                          ? [
                              ['Group CFO (full finance)', { ...EMPTY_PERMISSIONS, ...PRESET_GROUP_CFO }],
                              ['Clear All', { ...EMPTY_PERMISSIONS, ...FINANCE_PERMISSIONS }],
                            ]
                          : [
                              ['Executive (full)', PRESET_EXECUTIVE],
                              ['Branch Manager', PRESET_BRANCH_MGR],
                              ['Finance Only', PRESET_FINANCE],
                              ['Clear All', EMPTY_PERMISSIONS],
                            ]
                        ).map(([label, preset]) => (
                          <button key={label} style={S.presetBtn}
                            onClick={() => applyPreset(idx, preset)}
                          >{label}</button>
                        ))}
                      </div>
                    </Section>

                    <Section title="Module Permissions">
                      <table style={S.permTable}>
                        <thead>
                          <tr>
                            <th style={S.permTh}>Module</th>
                            <th style={{...S.permTh, textAlign:'center'}}>View</th>
                            <th style={{...S.permTh, textAlign:'center'}}>Export</th>
                            <th style={{...S.permTh, textAlign:'center'}}>Edit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(isFinance ? FINANCE_MODULE_LABELS : MODULE_LABELS).map(m => (
                            <tr key={m.key}>
                              <td style={S.permTd}>{m.label}</td>
                              <td style={{...S.permTd, textAlign:'center'}}>
                                <Toggle
                                  on={g.permissions[m.key]?.view}
                                  onChange={v => setGroupPerm(idx, m.key, 'view', v)}
                                />
                              </td>
                              <td style={{...S.permTd, textAlign:'center'}}>
                                {m.key !== 'actions' && m.key !== 'reports' && m.key !== 'notes'
                                  ? <Toggle on={g.permissions[m.key]?.export} onChange={v => setGroupPerm(idx, m.key, 'export', v)} />
                                  : <span style={{color:'#ccc'}}>—</span>
                                }
                              </td>
                              <td style={{...S.permTd, textAlign:'center'}}>
                                {m.hasEdit
                                  ? <Toggle on={g.permissions[m.key]?.edit} onChange={v => setGroupPerm(idx, m.key, 'edit', v)} />
                                  : <span style={{color:'#ccc'}}>—</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Section>
                  </div>
                );
              })()}

              {userGroups.length === 0 && (
                <div style={S.emptyState}>
                  No groups yet. Type a group name above and press Enter or click Add.
                </div>
              )}
            </div>
          )}

          {/* ════ STEP 4: USERS ════ */}
          {step === 4 && (
            <div>
              <StepHeader icon="👥" title="Users"
                desc="Add users and assign them to groups and branches. Magic link invites sent on launch." />

              <ImportBar
                title="Import from Excel"
                sub="FirstName, LastName, Email, Phone, UserGroup, BranchCode (or ALL), SendInvite"
                onImport={importUsers}
                templateName="InsightPro_User_Template.xlsx"
              />

              <Section title={`Users — ${users.length} added`}>
                <DataTable
                  cols={isFinance
                    ? ['Name','Email','Group','Brand','Invite','']
                    : ['Name','Email','Group','Branch Scope','Invite','']}
                  rows={users.map((u,i) => {
                    const isLinked = existingEmails[(u.email || '').trim().toLowerCase()] === true;
                    return [
                      `${u.firstName} ${u.lastName}`,
                      u.email,
                      u.groupId || '—',
                      isFinance
                        ? <Badge type={u.brand === 'Consolidated' ? 'green' : 'blue'}>{u.brand || 'Consolidated'}</Badge>
                        : (u.branchId || 'All Branches'),
                      isLinked
                        ? <Badge type="blue">Linked (existing)</Badge>
                        : (u.sendInvite
                            ? <Badge type="amber">Magic Link</Badge>
                            : <Badge type="grey">Manual</Badge>),
                      <button style={S.rowDel} onClick={() => setUsers(us => us.filter((_,j)=>j!==i))}>✕</button>
                    ];
                  })}
                />
                <Divider />
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr 1fr 1fr auto', gap:8, alignItems:'end'}}>
                  <Field label="First Name"><Input value={newUser.firstName} onChange={v => setNewUser(u=>({...u,firstName:v}))} /></Field>
                  <Field label="Last Name"><Input value={newUser.lastName} onChange={v => setNewUser(u=>({...u,lastName:v}))} /></Field>
                  <Field label="Email">
                    <Input
                      value={newUser.email}
                      onChange={v => { setNewUser(u=>({...u,email:v})); setNewUserEmailStatus('idle'); }}
                      onBlur={handleNewUserEmailBlur}
                    />
                  </Field>
                  <Field label="Phone"><Input value={newUser.phone} onChange={v => setNewUser(u=>({...u,phone:v}))} /></Field>
                  <Field label="Group">
                    <Select value={newUser.groupId} onChange={v => setNewUser(u=>({...u,groupId:v}))}
                      options={['', ...userGroups.map(g=>g.name)]} />
                  </Field>
                  {isFinance ? (
                    <Field label="Brand">
                      <Select value={newUser.brand} onChange={v => setNewUser(u=>({...u,brand:v}))}
                        options={BRAND_OPTIONS} />
                    </Field>
                  ) : (
                    <Field label="Branch">
                      <Select value={newUser.branchId} onChange={v => setNewUser(u=>({...u,branchId:v}))}
                        options={['ALL', ...branches.map(b=>b.code)]} />
                    </Field>
                  )}
                  <button style={S.addBtn} onClick={addUser}>+ Add</button>
                </div>
                {newUserEmailStatus !== 'idle' && (
                  <div style={{
                    marginTop:10, padding:'8px 12px', borderRadius:4,
                    fontFamily:'monospace', fontSize:10.5, letterSpacing:'0.04em',
                    background: newUserEmailStatus === 'existing'
                      ? 'rgba(37,99,235,0.08)'
                      : (newUserEmailStatus === 'checking' ? 'rgba(100,100,120,0.08)' : 'rgba(15,122,70,0.08)'),
                    border: '1px solid ' + (newUserEmailStatus === 'existing'
                      ? 'rgba(37,99,235,0.25)'
                      : (newUserEmailStatus === 'checking' ? 'rgba(100,100,120,0.2)' : 'rgba(15,122,70,0.25)')),
                    color: newUserEmailStatus === 'existing'
                      ? '#2563eb'
                      : (newUserEmailStatus === 'checking' ? '#888' : '#0f7a46'),
                  }}>
                    {newUserEmailStatus === 'checking'  && '⋯ Checking email…'}
                    {newUserEmailStatus === 'existing'  && '✓ Existing InsightPro user — will be linked to this company. No invite email will be sent.'}
                    {newUserEmailStatus === 'new'       && '+ New user — a magic-link invite will be sent on launch.'}
                  </div>
                )}
              </Section>
            </div>
          )}

          {/* ════ STEP 5: POWER BI & SCHEMA  (RetailPOS only) ════ */}
          {step === 5 && !isFinance && (
            <div>
              <StepHeader icon="⚡" title="Power BI & Data Schema"
                desc="Connect this client's Power BI dataset and map their column names." />

              <Section title="Power BI Dataset Connection">
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:12}}>
                  <Field label="Workspace ID *">
                    <Input value={pbi.workspaceId} onChange={v => setPbi(p=>({...p,workspaceId:v}))}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Field>
                  <Field label="Dataset ID *">
                    <Input value={pbi.datasetId} onChange={v => setPbi(p=>({...p,datasetId:v}))}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Field>
                </div>
                <Field label="Service Account (InsightPro account added to their PBI workspace)">
                  <Input value={pbi.serviceAccount} onChange={v => setPbi(p=>({...p,serviceAccount:v}))}
                    placeholder="client@insightpro.co.za" />
                </Field>
                <div style={{marginTop:10, padding:'10px 14px', background:'rgba(200,149,32,0.06)', border:'1px solid rgba(200,149,32,0.2)', borderRadius:5, fontSize:11.5, color:'#9898b0', lineHeight:1.6}}>
                  💡 Add the service account as a <strong style={{color:'#c89520'}}>Viewer</strong> on the client's Power BI workspace before launching.
                  Workspace ID is in the workspace URL. Dataset ID is in Settings → Datasets.
                </div>
              </Section>

              <Divider />

              <Section title="Data Schema Mapping">
                <div style={{marginBottom:12, padding:'10px 14px', background:'rgba(26,173,99,0.06)', border:'1px solid rgba(26,173,99,0.2)', borderRadius:5, fontSize:11.5, color:'#9898b0', lineHeight:1.6}}>
                  ✓ Pre-filled with standard column names. Only change values that differ in this client's Power BI dataset.
                </div>

                <div style={{fontFamily:'monospace', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8, marginTop:4}}>Transactions Table</div>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:8, marginBottom:14}}>
                  {[['txTable','Table name'],['dateCol','Date column'],['salesCol','Sales/Revenue'],['purchasesCol','Purchases'],['unitsCol','Units sold'],['codeCol','Stock code']].map(([k,l]) => (
                    <Field key={k} label={l}><Input value={pbi.schema[k]} onChange={v => setPbi(p=>({...p,schema:{...p.schema,[k]:v}}))} /></Field>
                  ))}
                </div>

                <div style={{fontFamily:'monospace', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Branch Table</div>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap:8, marginBottom:14}}>
                  {[['branchTable','Table name'],['branchNameCol','Branch name'],['branchConoCol','Branch ID/CONO']].map(([k,l]) => (
                    <Field key={k} label={l}><Input value={pbi.schema[k]} onChange={v => setPbi(p=>({...p,schema:{...p.schema,[k]:v}}))} /></Field>
                  ))}
                </div>

                <div style={{fontFamily:'monospace', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Stock Tables</div>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:8, marginBottom:14}}>
                  {[
                    ['stockTable','Stock table'],['stockCodeCol','Code col'],['stockDescCol','Description'],['stockCostCol','Avg cost'],
                    ['stockBalTable','Balances table'],['stockBalCodeCol','Bal code col'],['stockBalOnHandCol','On hand ⚠ AUTH'],['stockBalConoCol','Branch CONO'],
                  ].map(([k,l]) => (
                    <Field key={k} label={l}><Input value={pbi.schema[k]} onChange={v => setPbi(p=>({...p,schema:{...p.schema,[k]:v}}))} /></Field>
                  ))}
                </div>

                <div style={{fontFamily:'monospace', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Debtors Table</div>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:8, marginBottom:14}}>
                  {[
                    ['debtorTable','Table name'],['debtorAcctCol','Account col'],['debtorNameCol','Name col'],['debtorTotalCol','Total balance'],
                    ['debtorCurrentCol','Current (0-30d)'],['debtor60Col','60 days'],['debtor90Col','90 days'],['debtor180Col','180+ days'],
                    ['debtorOnHoldCol','On hold flag'],['debtorCrLimitCol','Credit limit'],['debtorLastPayCol','Last pay date'],['debtorLastAmtCol','Last pay amount'],
                  ].map(([k,l]) => (
                    <Field key={k} label={l}><Input value={pbi.schema[k]} onChange={v => setPbi(p=>({...p,schema:{...p.schema,[k]:v}}))} /></Field>
                  ))}
                </div>

                <div style={{fontFamily:'monospace', fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:8}}>Departments & Date Tables</div>
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:8}}>
                  {[
                    ['deptTable','Dept table'],['deptCodeCol','Dept code'],['deptNameCol','Dept name'],
                    ['dateTable','Date table'],['dateYYYYMMCol','YYYYMM col'],['dateMonthNameCol','Month name'],['dateYearCol','Year col'],
                  ].map(([k,l]) => (
                    <Field key={k} label={l}><Input value={pbi.schema[k]} onChange={v => setPbi(p=>({...p,schema:{...p.schema,[k]:v}}))} /></Field>
                  ))}
                </div>
              </Section>
            </div>
          )}

          {/* ════ STEP 5 (Finance): CHART OF ACCOUNTS ════ */}
          {step === 5 && isFinance && (
            <div>
              <StepHeader icon="📒" title="Chart of Accounts"
                desc="Upload the consolidated chart of accounts. Used to classify GL postings and to drive sign normalisation in the data pipeline." />

              <ImportBar
                title="Import from Excel"
                sub="GLAccount, GLDescription, AccountType, ReportCategory, Brand, NormalSign"
                onImport={importGlAccounts}
                templateName="InsightPro_ChartOfAccounts_Template.xlsx"
              />

              <Section title={`Chart of Accounts — ${glAccounts.length} entries`}>
                {glAccounts.length === 0 ? (
                  <div style={S.emptyState}>
                    Upload the chart of accounts xlsx. One row per (Brand, GLAccount).
                    Account types: Revenue / Cost of Sales / Other Expense / Other Income / Equity / etc.
                    NormalSign is +1 for accounts shown positive in the P&L, −1 if Sage stores them with flipped sign.
                  </div>
                ) : (
                  <DataTable
                    cols={['Brand','GL','Description','Account Type','Report Category','Sign','']}
                    rows={glAccounts.slice(0, 100).map((a,i) => [
                      <Badge type={a.brand === 'Pedros' ? 'blue' : a.brand === 'Wackys' ? 'amber' : 'green'}>{a.brand}</Badge>,
                      <code style={{fontSize:11}}>{a.glAccount}</code>,
                      a.glDescription,
                      a.accountType,
                      a.reportCategory || '—',
                      a.normalSign,
                      <button style={S.rowDel} onClick={() => setGlAccounts(g => g.filter((_,j)=>j!==i))}>✕</button>
                    ])}
                  />
                )}
                {glAccounts.length > 100 && (
                  <div style={{marginTop:8, fontSize:11, color:'#9898b0'}}>
                    Showing first 100 rows. {glAccounts.length - 100} more entries will be ingested on launch.
                  </div>
                )}
              </Section>

              <div style={{marginTop:12, padding:'10px 14px', background:'rgba(200,149,32,0.06)', border:'1px solid rgba(200,149,32,0.2)', borderRadius:5, fontSize:11.5, color:'#9898b0', lineHeight:1.6}}>
                💡 Power BI fields are not used for Multi-Entity Finance clients. Dashboards are served from BigQuery via the platform's data-API.
              </div>
            </div>
          )}

          {/* ════ STEP 6: REPORTS  (RetailPOS only — finance uses built-in pages) ════ */}
          {step === 6 && !isFinance && (
            <div>
              <StepHeader icon="📊" title="Reports & Dashboards"
                desc="Link Power BI reports and assign them to user groups." />

              <ImportBar
                title="Import from Excel"
                sub="ReportName, PowerBIReportID, PowerBIWorkspaceID, VisibleToGroups, DefaultForGroups"
                onImport={importReports}
                templateName="InsightPro_Report_Template.xlsx"
              />

              <Section title={`Reports — ${reports.length} configured`}>
                <DataTable
                  cols={['Report Name','Report ID','Visible To','']}
                  rows={reports.map((r,i) => [
                    r.name,
                    <code style={{fontSize:11}}>{r.powerBiReportId}</code>,
                    r.visibleToGroups.join(', ') || 'All',
                    <button style={S.rowDel} onClick={() => setReports(rs => rs.filter((_,j)=>j!==i))}>✕</button>
                  ])}
                />
                <Divider />
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 2fr auto', gap:8, alignItems:'end'}}>
                  <Field label="Name"><Input value={newReport.name} onChange={v => setNewReport(r=>({...r,name:v}))} placeholder="Sales Dashboard" /></Field>
                  <Field label="Power BI Report ID"><Input value={newReport.powerBiReportId} onChange={v => setNewReport(r=>({...r,powerBiReportId:v}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></Field>
                  <Field label="Workspace ID"><Input value={newReport.powerBiWorkspaceId} onChange={v => setNewReport(r=>({...r,powerBiWorkspaceId:v}))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" /></Field>
                  <button style={S.addBtn} onClick={addReport}>+ Add</button>
                </div>
              </Section>
            </div>
          )}

          {/* Step 6 (Finance): no Power BI — built-in pages serve the data */}
          {step === 6 && isFinance && (
            <div>
              <StepHeader icon="📊" title="Reports & Dashboards"
                desc="Multi-Entity Finance clients use the platform's built-in pages — no Power BI configuration needed." />
              <div style={{marginTop:8, padding:'14px 18px', background:'rgba(26,173,99,0.06)', border:'1px solid rgba(26,173,99,0.2)', borderRadius:6, fontSize:13, color:'#9898b0', lineHeight:1.7}}>
                The following pages are part of the platform and visible to any group with the matching permissions enabled in Step 3:
                <ul style={{margin:'10px 0 0 18px', padding:0}}>
                  {FINANCE_MODULE_LABELS.map(m => (
                    <li key={m.key} style={{marginBottom:4}}>{m.label}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* ════ STEP 7: SECURITY ════ */}
          {step === 7 && (
            <div>
              <StepHeader icon="🛡️" title="Security & Access Policy"
                desc="Company-wide security rules. Company Admin can adjust within these limits." />

              <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:16}}>
                <SecurityCard title="🔒 Authentication">
                  <ToggleRow label="MFA Required" sub="Force all users to enable 2FA"
                    on={security.mfaRequired} onChange={v => setSecurity(s=>({...s,mfaRequired:v}))} />
                  <ToggleRow label="Magic Link Only" sub="Disable password login"
                    on={security.magicLinkOnly} onChange={v => setSecurity(s=>({...s,magicLinkOnly:v}))} />
                  <NumRow label="Session Timeout (mins)" unit="mins"
                    value={security.sessionTimeoutMins} onChange={v => setSecurity(s=>({...s,sessionTimeoutMins:+v}))} />
                  <NumRow label="Max Failed Logins" unit="attempts"
                    value={security.maxFailedLogins} onChange={v => setSecurity(s=>({...s,maxFailedLogins:+v}))} />
                </SecurityCard>

                <SecurityCard title="📤 Data & Export">
                  <ToggleRow label="Allow Data Export" sub="Overrides group-level export"
                    on={security.exportAllowed} onChange={v => setSecurity(s=>({...s,exportAllowed:v}))} />
                  <ToggleRow label="Audit Log" sub="Track all user actions"
                    on={security.auditEnabled} onChange={v => setSecurity(s=>({...s,auditEnabled:v}))} />
                  <NumRow label="Audit Retention" unit="days"
                    value={security.auditRetentionDays} onChange={v => setSecurity(s=>({...s,auditRetentionDays:+v}))} />
                </SecurityCard>

                <SecurityCard title="🕐 Login Hours">
                  <ToggleRow label="Restrict Login Hours" sub="Block logins outside business hours"
                    on={security.loginHoursEnabled} onChange={v => setSecurity(s=>({...s,loginHoursEnabled:v}))} />
                  <div style={{display:'flex', gap:12, marginTop:8}}>
                    <Field label="Start">
                      <input type="time" value={security.loginHoursStart} style={S.input}
                        onChange={e => setSecurity(s=>({...s,loginHoursStart:e.target.value}))} />
                    </Field>
                    <Field label="End">
                      <input type="time" value={security.loginHoursEnd} style={S.input}
                        onChange={e => setSecurity(s=>({...s,loginHoursEnd:e.target.value}))} />
                    </Field>
                  </div>
                </SecurityCard>

                <SecurityCard title="🌐 IP Restrictions">
                  <ToggleRow label="IP Whitelist" sub="Only allow listed IPs"
                    on={security.ipWhitelist.length > 0} onChange={() => {}} />
                  <Field label="Allowed IPs (one per line)">
                    <textarea
                      rows={4} style={{...S.input, resize:'vertical'}}
                      placeholder={"196.25.1.100\n196.25.1.101\n196.25.0.0/24"}
                      value={security.ipWhitelist.join('\n')}
                      onChange={e => setSecurity(s=>({...s, ipWhitelist: e.target.value.split('\n').map(l=>l.trim()).filter(Boolean)}))}
                    />
                  </Field>
                </SecurityCard>
              </div>
            </div>
          )}

          {/* ════ STEP 7: REVIEW & LAUNCH ════ */}
          {step === 8 && (
            <div>
              <StepHeader icon="🚀" title="Review & Launch"
                desc="Everything looks good. Review the summary and click Launch to provision the client." />

              <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:20}}>
                <ReviewCard title="Company">
                  <ReviewRow k="Name" v={company.name} />
                  <ReviewRow k="Solution" v={isFinance ? 'Multi-Entity Finance' : 'Retail (POS)'} />
                  <ReviewRow k="Plan" v={`${company.plan.charAt(0).toUpperCase()+company.plan.slice(1)}`} />
                  <ReviewRow k="Dataset" v={`insightpro_${companySlug}`} mono />
                  <ReviewRow k="Logo" v={company.logoUrl ? '✓ Uploaded' : '— None'} />
                </ReviewCard>
                <ReviewCard title={isFinance ? 'Legal Entities' : 'Branches'}>
                  <ReviewRow k="Total" v={branches.length} />
                  {isFinance
                    ? BRAND_OPTIONS.filter(b => b !== 'Consolidated').map(b => (
                        <ReviewRow key={b} k={b} v={branches.filter(x=>x.brand===b).length} />
                      ))
                    : ['Store','Warehouse','Office'].map(t => (
                        <ReviewRow key={t} k={`${t}s`} v={branches.filter(b=>b.type===t).length} />
                      ))
                  }
                </ReviewCard>
                <ReviewCard title="User Groups">
                  {userGroups.map(g => <ReviewRow key={g.name} k={g.name} v={g.branchScope} />)}
                  {userGroups.length === 0 && <div style={{color:'#999',fontSize:12}}>No groups defined</div>}
                </ReviewCard>
                <ReviewCard title="Users">
                  <ReviewRow k="Total" v={users.length} />
                  <ReviewRow k="Magic link invites" v={users.filter(u=>u.sendInvite).length} />
                </ReviewCard>
                {!isFinance && (
                  <ReviewCard title="Power BI & Schema">
                    <ReviewRow k="Workspace ID" v={pbi.workspaceId || '—'} mono />
                    <ReviewRow k="Dataset ID"   v={pbi.datasetId   || '—'} mono />
                    <ReviewRow k="Service Acct" v={pbi.serviceAccount || '—'} />
                    <ReviewRow k="Schema fields" v={Object.keys(pbi.schema).length + ' mapped'} />
                  </ReviewCard>
                )}
                {isFinance && (
                  <ReviewCard title="Chart of Accounts">
                    <ReviewRow k="GL accounts" v={glAccounts.length} />
                    {BRAND_OPTIONS.filter(b => b !== 'Consolidated').map(b => (
                      <ReviewRow key={b} k={b} v={glAccounts.filter(a=>a.brand===b).length} />
                    ))}
                  </ReviewCard>
                )}
                {!isFinance && (
                  <ReviewCard title="Reports">
                    {reports.map(r => <ReviewRow key={r.powerBiReportId} k={r.name} v={r.visibleToGroups.join(', ') || 'All'} />)}
                    {reports.length === 0 && <div style={{color:'#999',fontSize:12}}>No reports configured</div>}
                  </ReviewCard>
                )}
                {isFinance && (
                  <ReviewCard title="Built-in Pages">
                    {FINANCE_MODULE_LABELS.map(m => <ReviewRow key={m.key} k={m.label} v="enabled" />)}
                  </ReviewCard>
                )}
                <ReviewCard title="Security">
                  <ReviewRow k="MFA" v={security.mfaRequired ? 'Required' : 'Optional'} />
                  <ReviewRow k="Session Timeout" v={`${security.sessionTimeoutMins} mins`} />
                  <ReviewRow k="Audit Log" v={`${security.auditRetentionDays} days`} />
                </ReviewCard>
              </div>

              {launchError && (
                <div style={S.errorBox}>⚠ {launchError}</div>
              )}

              <div style={S.launchBox}>
                <div style={{fontSize:28}}>🚀</div>
                <div style={{flex:1}}>
                  <div style={S.launchTitle}>Ready to Launch</div>
                  <div style={S.launchSub}>
                    Clicking Launch will create the BigQuery dataset, set up Firebase Auth accounts,
                    assign permissions and send {users.filter(u=>u.sendInvite).length} magic link invite(s).
                  </div>
                  <div style={{display:'flex', flexWrap:'wrap', gap:6, marginTop:10}}>
                    {['BigQuery dataset','Firebase Auth accounts','Custom claims','Firestore records','PBI schema configured',
                      `${users.filter(u=>u.sendInvite).length} magic link emails`].map(item => (
                      <span key={item} style={S.launchTag}>✓ {item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ── Footer Nav ── */}
        <div style={{...S.footer, flexWrap:'wrap', gap:8, padding: isMobile ? '0 16px' : '0 40px'}}>
          <button style={S.btnBack} onClick={() => setStep(s => Math.max(1, s-1))}>← Back</button>
          <div style={S.progressBar}>
            <div style={{...S.progressFill, width:`${pct}%`}} />
          </div>
          <div style={S.stepCounter}>Step {step} of {STEPS.length}</div>
          {step < STEPS.length ? (
            <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4}}>
              {currentStepError && (
                <div style={{fontSize:10.5, color:'#c94a4a', fontFamily:'monospace', textAlign:'right', maxWidth:220}}>
                  ⚠ {currentStepError}
                </div>
              )}
              <button
                style={{
                  ...S.btnNext,
                  ...(canAdvance ? {} : { opacity: 0.4, cursor: 'not-allowed', background: '#9898b0' }),
                }}
                onClick={handleNext}
              >Continue →</button>
            </div>
          ) : (
            <button style={S.btnLaunch} onClick={handleLaunch} disabled={launching}>
              {launching ? '⏳ Launching…' : '🚀 Launch Client'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────

function LaunchSuccess({ data }) {
  return (
    <div style={{padding:40, maxWidth:640, margin:'0 auto'}}>
      <div style={{fontSize:48, marginBottom:16}}>🎉</div>
      <h2 style={{fontSize:24, fontWeight:700, marginBottom:8}}>Client Launched Successfully</h2>
      <p style={{color:'#666', marginBottom:24}}>The client account has been provisioned and invites sent.</p>
      <div style={{background:'#f8fdf9', border:'1px solid #d1fae5', borderRadius:8, padding:20, marginBottom:16}}>
        <div style={{fontFamily:'monospace', fontSize:12, lineHeight:2}}>
          {data.log?.map((l,i) => <div key={i}>{l}</div>)}
        </div>
      </div>
      {data.inviteResults?.length > 0 && (
        <div style={{marginTop:16}}>
          <div style={{fontWeight:600, marginBottom:8}}>Invite Results:</div>
          {data.inviteResults.map((r,i) => (
            <div key={i} style={{display:'flex', gap:8, fontSize:12, padding:'4px 0'}}>
              <span style={{color: r.status === 'invited' ? '#16a34a' : '#dc2626'}}>
                {r.status === 'invited' ? '✓' : '✗'}
              </span>
              <span>{r.email}</span>
              {r.error && <span style={{color:'#dc2626'}}>— {r.error}</span>}
            </div>
          ))}
        </div>
      )}
      <button style={{...S.btnNext, marginTop:24}} onClick={() => window.location.href='/admin'}>
        Back to Admin Dashboard
      </button>
    </div>
  );
}

function StepHeader({ icon, title, desc }) {
  return (
    <div style={{marginBottom:24}}>
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:6}}>
        <div style={S.stepIcon}>{icon}</div>
        <h2 style={{fontSize:20, fontWeight:700, letterSpacing:'-0.02em'}}>{title}</h2>
      </div>
      <p style={{fontSize:12.5, color:'#5a5a72', marginLeft:50, lineHeight:1.5}}>{desc}</p>
    </div>
  );
}

function ImportBar({ title, sub, onImport, templateName }) {
  return (
    <div style={S.importBar}>
      <div style={{fontSize:20}}>📊</div>
      <div style={{flex:1}}>
        <div style={{fontSize:12, fontWeight:600, color:'#f0f0f8'}}>{title}</div>
        <div style={{fontSize:10.5, color:'rgba(255,255,255,0.3)', marginTop:2}}>{sub}</div>
      </div>
      <label style={S.btnImport}>
        ⬆ Upload Excel
        <input type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={onImport} />
      </label>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:24}}>
      <div style={S.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Grid({ cols = 2, children, style }) {
  return <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:14, ...style }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{display:'flex', flexDirection:'column', gap:5}}>
      <label style={{fontSize:11, fontWeight:600, color:'#5a5a72'}}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, onBlur, placeholder, disabled }) {
  return (
    <input
      value={value} placeholder={placeholder}
      disabled={disabled}
      onChange={e => onChange?.(e.target.value)}
      onBlur={onBlur}
      style={{ ...S.input, ...(disabled ? {background:'#f4f5f8', color:'#999'} : {}) }}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={S.input}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={() => onChange(!on)}
      style={{
        width:20, height:20, borderRadius:4,
        border:`1.5px solid ${on ? 'rgba(15,122,70,0.5)' : '#e4e6ec'}`,
        background: on ? 'rgba(15,122,70,0.1)' : '#fff',
        display:'inline-flex', alignItems:'center', justifyContent:'center',
        cursor:'pointer', fontSize:11, color: on ? '#0f7a46' : 'transparent',
        transition:'all 0.15s',
      }}
    >{on ? '✓' : ''}</div>
  );
}

function ToggleRow({ label, sub, on, onChange }) {
  return (
    <div style={S.toggleRow}>
      <div>
        <div style={{fontSize:12, color:'#5a5a72'}}>{label}</div>
        {sub && <div style={{fontSize:10, color:'#9898b0', marginTop:1}}>{sub}</div>}
      </div>
      <div onClick={() => onChange(!on)} style={{ ...S.sw, background: on ? '#1aad63' : '#e4e6ec' }}>
        <div style={{ ...S.swThumb, transform: on ? 'translateX(16px)' : 'translateX(0)' }} />
      </div>
    </div>
  );
}

function NumRow({ label, unit, value, onChange }) {
  return (
    <div style={S.toggleRow}>
      <div style={{fontSize:12, color:'#5a5a72'}}>{label}</div>
      <div style={{display:'flex', alignItems:'center', gap:6}}>
        <input type="number" value={value} onChange={e => onChange(e.target.value)}
          style={{width:60, padding:'4px 8px', border:'1px solid #e4e6ec', borderRadius:4, fontFamily:'monospace', fontSize:11, textAlign:'center'}} />
        <span style={{fontSize:10.5, color:'#9898b0'}}>{unit}</span>
      </div>
    </div>
  );
}

function SecurityCard({ title, children }) {
  return (
    <div style={S.secCard}>
      <div style={S.secCardTitle}>{title}</div>
      {children}
    </div>
  );
}

function DataTable({ cols, rows }) {
  return (
    <table style={{width:'100%', borderCollapse:'collapse'}}>
      <thead>
        <tr>{cols.map(c => <th key={c} style={S.th}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={cols.length} style={{padding:20, textAlign:'center', color:'#9898b0', fontSize:12}}>No records yet</td></tr>
          : rows.map((row,i) => (
            <tr key={i} style={{background: i%2===0 ? '#fff' : '#fafbfd'}}>
              {row.map((cell,j) => <td key={j} style={S.td}>{cell}</td>)}
            </tr>
          ))
        }
      </tbody>
    </table>
  );
}

function ReviewCard({ title, children }) {
  return (
    <div style={{background:'#fff', border:'1px solid #e4e6ec', borderRadius:8, padding:16}}>
      <div style={{fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.14em', textTransform:'uppercase', color:'#9898b0', marginBottom:10}}>{title}</div>
      {children}
    </div>
  );
}

function ReviewRow({ k, v, mono }) {
  return (
    <div style={{display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12, borderBottom:'1px solid #f4f5f8'}}>
      <span style={{color:'#5a5a72'}}>{k}</span>
      <span style={{fontWeight:500, fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? 11 : 12}}>{v}</span>
    </div>
  );
}

function Badge({ type, children }) {
  const colors = {
    blue:  {bg:'rgba(37,99,235,0.1)',  color:'#2563eb'},
    amber: {bg:'rgba(196,122,26,0.1)', color:'#c47a1a'},
    green: {bg:'rgba(15,122,70,0.1)',  color:'#0f7a46'},
    grey:  {bg:'rgba(100,100,120,0.1)',color:'#666'},
  };
  const c = colors[type] || colors.grey;
  return (
    <span style={{
      display:'inline-block', padding:'2px 8px', borderRadius:3,
      fontFamily:'monospace', fontSize:8, letterSpacing:'0.08em', textTransform:'uppercase',
      background:c.bg, color:c.color,
    }}>{children}</span>
  );
}

function Divider() {
  return <div style={{height:1, background:'#e4e6ec', margin:'16px 0'}} />;
}

// ────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────
const S = {
  shell:        { display:'flex', minHeight:'calc(100vh - 54px)', fontFamily:"'Instrument Sans', sans-serif" },
  sidebar:      { width:260, minWidth:260, background:'#111116', borderRight:'1px solid rgba(255,255,255,0.07)', padding:'28px 0', display:'flex', flexDirection:'column' },
  sidebarLabel: { fontFamily:'monospace', fontSize:8, letterSpacing:'0.18em', textTransform:'uppercase', color:'rgba(255,255,255,0.2)', padding:'0 24px', marginBottom:20 },
  stepItem:     { display:'flex', alignItems:'flex-start', gap:12, padding:'10px 24px', cursor:'pointer', position:'relative', transition:'background 0.15s' },
  stepActive:   { background:'rgba(200,149,32,0.06)', borderLeft:'2px solid #c89520' },
  stepDone:     { borderLeft:'2px solid #1aad63' },
  stepNum:      { width:24, height:24, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'monospace', fontSize:10, fontWeight:500, flexShrink:0, marginTop:1, border:'1px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.25)', transition:'all 0.2s' },
  stepNumActive:{ background:'#c89520', color:'#0a0a0f', border:'1px solid #c89520', fontWeight:700 },
  stepNumDone:  { background:'#1aad63', color:'#fff', border:'1px solid #1aad63' },
  stepTitle:    { fontSize:12.5, fontWeight:500, color:'rgba(255,255,255,0.3)', marginBottom:2 },
  stepTitleActive: { color:'#f0f0f8', fontWeight:600 },
  stepTitleDone:   { color:'rgba(255,255,255,0.5)' },
  stepDesc:     { fontSize:10.5, color:'rgba(255,255,255,0.18)', lineHeight:1.4 },
  connector:    { width:1, height:8, background:'rgba(255,255,255,0.07)', margin:'0 0 0 35px' },
  sidebarFooter:{ marginTop:'auto', padding:'20px 24px 0', borderTop:'1px solid rgba(255,255,255,0.07)' },
  summaryLabel: { fontFamily:'monospace', fontSize:8, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(255,255,255,0.35)', marginBottom:6 },
  summaryText:  { fontFamily:'monospace', fontSize:9, color:'rgba(255,255,255,0.25)', lineHeight:2 },

  main:         { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  content:      { flex:1, padding:'32px 40px', overflowY:'auto', maxHeight:'calc(100vh - 54px - 72px)', background:'#f4f5f8' },
  stepIcon:     { fontSize:22, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(200,149,32,0.1)', borderRadius:8, flexShrink:0 },

  footer:       { height:72, background:'#fff', borderTop:'1px solid #e4e6ec', display:'flex', alignItems:'center', padding:'0 40px', gap:12 },
  progressBar:  { flex:1, height:3, background:'#e4e6ec', borderRadius:2, overflow:'hidden' },
  progressFill: { height:'100%', background:'linear-gradient(90deg,#c89520,#e0ad38)', borderRadius:2, transition:'width 0.4s ease' },
  stepCounter:  { fontFamily:'monospace', fontSize:10, color:'#9898b0', whiteSpace:'nowrap' },
  btnBack:      { padding:'9px 20px', borderRadius:5, border:'1px solid #e4e6ec', background:'transparent', fontSize:12.5, cursor:'pointer', color:'#5a5a72' },
  btnNext:      { padding:'9px 24px', borderRadius:5, background:'#c89520', border:'none', fontSize:12.5, fontWeight:600, cursor:'pointer', color:'#0a0a0f' },
  btnLaunch:    { padding:'9px 28px', borderRadius:5, background:'#1aad63', border:'none', fontSize:12.5, fontWeight:700, cursor:'pointer', color:'#fff' },

  sectionTitle: { fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.16em', textTransform:'uppercase', color:'#9898b0', marginBottom:14, paddingBottom:8, borderBottom:'1px solid #e4e6ec' },
  input:        { padding:'9px 12px', background:'#fff', border:'1px solid #e4e6ec', borderRadius:5, fontFamily:"'Instrument Sans', sans-serif", fontSize:12.5, color:'#1a1a2e', outline:'none', width:'100%' },
  importBar:    { display:'flex', alignItems:'center', gap:10, background:'#111116', border:'1px solid rgba(255,255,255,0.07)', borderRadius:6, padding:'12px 16px', marginBottom:24 },
  btnImport:    { padding:'7px 14px', background:'transparent', border:'1px solid rgba(200,149,32,0.3)', borderRadius:4, fontFamily:'monospace', fontSize:9.5, letterSpacing:'0.08em', textTransform:'uppercase', color:'#c89520', cursor:'pointer', whiteSpace:'nowrap' },

  addBtn:       { padding:'9px 18px', background:'#c89520', border:'none', borderRadius:5, fontWeight:600, fontSize:12, cursor:'pointer', color:'#0a0a0f', alignSelf:'flex-end' },
  rowDel:       { padding:'3px 8px', borderRadius:3, border:'1px solid #e4e6ec', background:'transparent', fontSize:10, cursor:'pointer', color:'#9898b0' },
  th:           { fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.1em', textTransform:'uppercase', color:'#9898b0', textAlign:'left', padding:'8px 10px', background:'#f8f9fc', borderBottom:'1px solid #e4e6ec' },
  td:           { padding:'9px 10px', fontSize:12, color:'#1a1a2e', borderBottom:'1px solid #f0f1f6' },
  emptyState:   { padding:40, textAlign:'center', color:'#9898b0', fontSize:12, border:'1px dashed #e4e6ec', borderRadius:8 },

  planCard:     { border:'1px solid #e4e6ec', borderRadius:8, padding:16, cursor:'pointer', background:'#fff', transition:'all 0.15s' },
  planSelected: { border:'1px solid #c89520', background:'rgba(200,149,32,0.05)', boxShadow:'0 0 0 3px rgba(200,149,32,0.1)' },
  planName:     { fontSize:13, fontWeight:700, color:'#1a1a2e', marginBottom:4 },
  planPrice:    { fontFamily:'monospace', fontSize:11, color:'#c89520', marginBottom:8 },
  planFeatures: { fontSize:10.5, color:'#5a5a72', lineHeight:1.7, whiteSpace:'pre-line' },

  tabs:         { display:'flex', gap:0, borderBottom:'1px solid #e4e6ec', marginBottom:20 },
  tab:          { padding:'8px 16px', fontSize:12.5, color:'#5a5a72', cursor:'pointer', borderBottom:'2px solid transparent', display:'flex', alignItems:'center', gap:6 },
  tabActive:    { color:'#c89520', borderBottom:'2px solid #c89520', fontWeight:600 },
  tabInput:     { border:'none', outline:'none', fontSize:12.5, color:'#5a5a72', background:'transparent', width:120 },
  tabAddBtn:    { padding:'2px 8px', fontSize:10, background:'#c89520', color:'#0a0a0f', border:'none', borderRadius:3, cursor:'pointer', fontWeight:600 },

  scopeBtn:     { padding:'6px 14px', borderRadius:20, border:'1px solid #e4e6ec', fontSize:11.5, cursor:'pointer', color:'#5a5a72', background:'#fff', transition:'all 0.15s' },
  scopeBtnSel:  { background:'rgba(200,149,32,0.1)', border:'1px solid rgba(200,149,32,0.3)', color:'#c89520', fontWeight:600 },
  presetBtn:    { padding:'5px 12px', borderRadius:4, border:'1px solid #e4e6ec', fontSize:11, cursor:'pointer', background:'#fff', color:'#5a5a72', transition:'all 0.15s' },

  permTable:    { width:'100%', borderCollapse:'collapse' },
  permTh:       { fontFamily:'monospace', fontSize:8.5, letterSpacing:'0.1em', textTransform:'uppercase', color:'#9898b0', textAlign:'left', padding:'8px 12px', background:'#f8f9fc', borderBottom:'1px solid #e4e6ec' },
  permTd:       { padding:'10px 12px', borderBottom:'1px solid #f0f1f6', fontSize:12, color:'#1a1a2e', fontWeight:500 },

  secCard:      { background:'#fff', border:'1px solid #e4e6ec', borderRadius:8, padding:18 },
  secCardTitle: { fontSize:12, fontWeight:700, color:'#1a1a2e', marginBottom:14, paddingBottom:10, borderBottom:'1px solid #e4e6ec' },
  toggleRow:    { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f0f1f6' },
  sw:           { width:36, height:20, borderRadius:10, position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0 },
  swThumb:      { position:'absolute', width:14, height:14, borderRadius:'50%', background:'#fff', top:3, left:3, transition:'transform 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' },

  launchBox:    { background:'linear-gradient(135deg,rgba(200,149,32,0.08),rgba(200,149,32,0.03))', border:'1px solid rgba(200,149,32,0.3)', borderRadius:8, padding:20, display:'flex', alignItems:'flex-start', gap:16 },
  launchTitle:  { fontSize:14, fontWeight:700, color:'#1a1a2e', marginBottom:4 },
  launchSub:    { fontSize:11.5, color:'#5a5a72', lineHeight:1.5 },
  launchTag:    { display:'inline-flex', alignItems:'center', gap:4, fontFamily:'monospace', fontSize:9, padding:'3px 8px', borderRadius:3, background:'rgba(15,122,70,0.08)', color:'#0f7a46', border:'1px solid rgba(15,122,70,0.2)' },
  errorBox:     { background:'rgba(176,40,40,0.08)', border:'1px solid rgba(176,40,40,0.25)', borderRadius:6, padding:'12px 16px', fontSize:12, color:'#b02828', marginBottom:16 },
};
